import React from 'react'
import { Search, Clock, TrendingUp, HelpCircle } from 'lucide-react'
import { SearchSuggestion } from '../../utils/search.utils'

interface SearchSuggestionsProps {
  suggestions: SearchSuggestion[]
  popularSearches: string[]
  onSuggestionClick: (suggestion: string) => void
  onPopularSearchClick: (search: string) => void
  isVisible: boolean
  className?: string
}

const getSuggestionIcon = (type: SearchSuggestion['type']) => {
  switch (type) {
    case 'question':
      return <HelpCircle className="h-4 w-4 text-blue-500" />
    case 'keyword':
      return <Search className="h-4 w-4 text-green-500" />
    case 'category':
      return <TrendingUp className="h-4 w-4 text-purple-500" />
    default:
      return <Search className="h-4 w-4 text-muted-foreground" />
  }
}

const getSuggestionLabel = (type: SearchSuggestion['type']) => {
  switch (type) {
    case 'question':
      return 'Question'
    case 'keyword':
      return 'Keyword'
    case 'category':
      return 'Category'
    default:
      return 'Search'
  }
}

export const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  suggestions,
  popularSearches,
  onSuggestionClick,
  onPopularSearchClick,
  isVisible,
  className = ''
}) => {
  if (!isVisible) return null

  const hasSuggestions = suggestions.length > 0
  const hasPopular = popularSearches.length > 0

  return (
    <div className={`absolute top-full left-0 right-0 z-50 mt-2 ${className}`}>
      <div className="bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-y-auto">
        {/* Search Suggestions */}
        {hasSuggestions && (
          <div className="p-3">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Suggestions
            </div>
            <div className="space-y-1">
              {suggestions.map((suggestion, index) => (
                <button
                  key={`suggestion-${index}`}
                  onClick={() => onSuggestionClick(suggestion.text)}
                  className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-accent transition-colors text-left group"
                >
                  <div className="flex-shrink-0">
                    {getSuggestionIcon(suggestion.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground group-hover:text-accent-foreground truncate">
                      {suggestion.text}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {getSuggestionLabel(suggestion.type)} • Score: {Math.round(suggestion.score)}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <Search className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Divider */}
        {hasSuggestions && hasPopular && (
          <div className="border-t border-border" />
        )}

        {/* Popular Searches */}
        {hasPopular && (
          <div className="p-3">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Popular Searches
            </div>
            <div className="space-y-1">
              {popularSearches.slice(0, 5).map((search, index) => (
                <button
                  key={`popular-${index}`}
                  onClick={() => onPopularSearchClick(search)}
                  className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-accent transition-colors text-left group"
                >
                  <div className="flex-shrink-0">
                    <Clock className="h-4 w-4 text-orange-500" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground group-hover:text-accent-foreground truncate">
                      {search}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <Search className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No suggestions state */}
        {!hasSuggestions && !hasPopular && (
          <div className="p-4 text-center">
            <div className="text-muted-foreground text-sm">
              No suggestions available
            </div>
          </div>
        )}

        {/* Footer hint */}
        <div className="border-t border-border px-3 py-2 bg-muted/30">
          <div className="text-xs text-muted-foreground text-center">
            Press Enter to search • ESC to close
          </div>
        </div>
      </div>
    </div>
  )
}